import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';
import WatchProps, { BroadcastProps, PlaylistItem } from './type';
import { RichText, Image, UnderlineTitle } from '../common';
import LocalizationContext from '../../contexts/Localizations';
import VideoObject from '../VideoObject';
import './styles.scss';

const Broadcast = ({
  event,
  date,
  tz,
}: BroadcastProps) => {
  const { locale } = useContext(LocalizationContext);
  const dateTime = useMemo(() => moment(date).locale(locale), [date]);

  return (
    <li className="watch__broadcast">
      <p className="watch__broadcast-event">{event}</p>
      <div className="watch__broadcast-datetime">
        <span className="watch__broadcast-date">{dateTime.format('dddd DD MMM')}</span>
        <span className="watch__broadcast-time">
          {dateTime.format('HH:mm')}
          &nbsp;
          {tz}
        </span>
      </div>
    </li>
  );
};

const WatchOn = ({
  playlist,
  eventHub,
}: { playlist: PlaylistItem[], eventHub?: boolean }) => {
  const { translations } = useContext(LocalizationContext);
  const scrollBarRef = useRef<PerfectScrollbar>(null);
  const [selectedVideo, setSelectedVideo] = useState<PlaylistItem>(playlist[0]);
  const [closeVideo, setCloseVideo] = useState(false);

  const selectVideo = (event: React.MouseEvent, video: PlaylistItem) => {
    event.preventDefault();

    setCloseVideo(true);
    setSelectedVideo(video);
  };

  useEffect(() => {
    setTimeout(() => {
      if (scrollBarRef.current) {
        scrollBarRef.current.updateScroll();
      }
    }, 100);
  }, []);

  return (
    <div className="watch__content watch__content--race-mode">
      <div className="watch__playing">
        <VideoObject
          {...selectedVideo}
          eventHub={eventHub}
          closeVideo={closeVideo}
          setCloseVideo={setCloseVideo}
        />
      </div>
      <div className="watch__playlist">
        {eventHub && (
          <div className="watch__playlist-label">
            {translations.event.liveScheduleLabel}
          </div>
        )}
        <PerfectScrollbar className="watch__playlist-container" ref={scrollBarRef}>
          <ul className="watch__playlist-list">
            {playlist.map((video) => {
              const disciplineTitle = video.title?.toLowerCase().split(' ').join('-');
              return (
                <li className={`watch__playlist-item ${eventHub ? `watch__playlist-item--${disciplineTitle}` : ''}`} key={video.id}>
                  <button
                    className="watch__playlist-link"
                    onClick={(event: React.MouseEvent) => selectVideo(event, video)}
                  >
                    <div className="watch__playlist-thumb">
                      {video.isLive && (
                        <div className="watch__playlist-live">Live</div>
                      )}
                      {(!video.isLive && eventHub) && (
                        <div className="watch__playlist-time">{video.startTime}</div>
                      )}

                      {!eventHub && (
                        <Image url={video.thumbnailImageUrl} alt={video.description as string} />
                      )}
                    </div>
                    <div className="watch__playlist-title">
                      {video.title}
                    </div>
                    {eventHub && (
                      <div className="watch__playlist-description">
                        {video.description}
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </PerfectScrollbar>
      </div>
    </div>
  );
};

const Watch = ({
  title,
  content,
  sponsor,
  broadcasts,
  raceMode,
  playlist,
  eventHub,
}: WatchProps) => (
  <div className={`watch ${raceMode ? 'watch--race-mode' : ''} ${eventHub ? 'watch--event-hub' : ''}`} aria-label={title}>
    <div className="watch__container">
      <div className="watch__header">
        <UnderlineTitle className="watch__title" title={title} containerTheme="black" />

        {sponsor && (
          <div className="watch__sponsor">
            <Image url={sponsor.file.url} alt={sponsor.title} />
          </div>
        )}
      </div>

      {!raceMode && (
        <div className="watch__content">
          {content && (
            <div className="watch__text">
              <RichText content={content} containerTheme="black" />
            </div>
          )}

          {broadcasts && (
            <ul className="watch__broadcasts">
              {broadcasts.map((broadcast) => (
                <Broadcast key={broadcast.event} {...broadcast} />
              ))}
            </ul>
          )}
        </div>
      )}

      {(raceMode || eventHub) && playlist && (
        <WatchOn playlist={playlist} eventHub={eventHub} />
      )}
    </div>
  </div>
);

export default Watch;
